import React, { useState } from 'react';
import { 
  LayoutDashboard, Package, PlusCircle, TrendingDown, RefreshCw,
  ArrowLeftRight, Shield, BarChart3, History, FileText,
  Upload, FileUp, MapPin, FolderTree, Wrench, Trash2
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { FeatureModeToggle } from './feature-mode-toggle';
import type { FeatureModule } from '@/types/features';
import { cn } from '@/lib/utils';

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  LayoutDashboard,
  Package,
  PlusCircle,
  TrendingDown,
  RefreshCw,
  ArrowLeftRight,
  Shield,
  BarChart3,
  History,
  FileText,
  Upload,
  FileUp,
  MapPin,
  FolderTree,
  Wrench,
  Trash2,
  // HelpCircle removed - unused
};

interface FeatureModuleCardProps {
  module: FeatureModule;
  onModeChange: (moduleId: string, mode: 'demo' | 'live') => void;
  isLoading?: boolean;
  isRecentlyChanged?: boolean;
}

export const FeatureModuleCard: React.FC<FeatureModuleCardProps> = ({
  module,
  onModeChange,
  isLoading = false,
  isRecentlyChanged = false,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const IconComponent = iconMap[module.icon] || Package;
  
  const isDisabled = module.status === 'coming-soon' || module.apiRequired;

  const getStatusBadge = () => {
    if (module.status === 'coming-soon') {
      return (
        <Badge variant="warning" accent="warning" className="text-xs">
          Coming Soon
        </Badge>
      );
    }
    
    if (module.apiRequired) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge className="text-xs cursor-help bg-purple-100 text-purple-700 hover:bg-purple-200">
                Coming Soon
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs">This feature is coming soon</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return (
      <Badge 
        variant={module.mode === 'live' ? 'success' : 'secondary'} 
        accent={module.mode === 'live' ? 'success' : undefined}
        className="text-xs"
      >
        {module.mode === 'live' ? 'Live' : 'Demo'}
      </Badge>
    );
  };

  return (
    <Card 
      className={cn(
        'transition-all duration-300 hover:shadow-md h-full',
        isRecentlyChanged && 'ring-2 ring-primary shadow-lg animate-pulse'
      )}
    >
      <CardContent className="p-4">
        <div className={cn(
          'flex flex-col gap-3',
          isDisabled && 'opacity-60'
        )}>
          {/* Header with Icon and Toggle */}
          <div className="flex items-start justify-between gap-2">
            <div className={cn(
              'rounded-lg p-2 flex items-center justify-center shrink-0',
              module.mode === 'live' ? 'bg-primary/10' : 'bg-gray-100'
            )}>
              <IconComponent className={cn(
                'h-5 w-5',
                module.mode === 'live' ? 'text-primary' : 'text-gray-600'
              )} />
            </div>
            
            {/* Toggle Switch */}
            <div className="shrink-0">
              <TooltipProvider>
                <Tooltip open={isDisabled && showTooltip}>
                  <TooltipTrigger asChild>
                    <div
                      onMouseEnter={() => setShowTooltip(true)}
                      onMouseLeave={() => setShowTooltip(false)}
                    >
                      <FeatureModeToggle
                        mode={module.mode}
                        disabled={isDisabled}
                        loading={isLoading}
                        onChange={(mode) => onModeChange(module.id, mode)}
                      />
                    </div>
                  </TooltipTrigger>
                  {isDisabled && (
                    <TooltipContent>
                      <p className="text-xs">
                        {module.status === 'coming-soon' 
                          ? 'Coming soon' + (module.comingSoonDate ? ' - ' + module.comingSoonDate : '')
                          : 'Live mode requires API connection'
                        }
                      </p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>

          {/* Title and Badge */}
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-sm truncate">{module.name}</h3>
              {(module.status === 'coming-soon' || module.apiRequired) && (
                <div className="opacity-100">
                  {getStatusBadge()}
                </div>
              )}
            </div>
            
            <p className="text-xs text-muted-foreground line-clamp-2">
              {module.description}
            </p>
          </div>

          {/* Usage Stats (if available) */}
          {module.usageStats && (
            <div className="pt-3 border-t border-border">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-muted-foreground">Demo:</span>
                  <span className="ml-1 font-medium">{module.usageStats.demoUsage}%</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Live:</span>
                  <span className="ml-1 font-medium">{module.usageStats.liveUsage}%</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
